import { ERC721TokenContract } from '@0x/contract-wrappers';
import { signatureUtils } from '@0x/order-utils';
import { MetamaskSubprovider } from '@0x/subproviders';
import { BigNumber } from '@0x/utils';
import { createAction } from 'typesafe-actions';
import axios from 'axios';

import { COLLECTIBLE_ADDRESS, ZERO, ALL_USER_TRADE_HISTORY_URL, TRADE_HISTORY_URL, USER_TRADE_HISTORY_URL } from '../../common/constants';
import { InsufficientOrdersAmountException } from '../../exceptions/insufficient_orders_amount_exception';
import { InsufficientTokenBalanceException } from '../../exceptions/insufficient_token_balance_exception';
import { SignedOrderException } from '../../exceptions/signed_order_exception';
import { isWeth } from '../../util/known_tokens';
import { buildLimitOrder, buildMarketOrders, isDutchAuction } from '../../util/orders';
import {
    createBasicBuyCollectibleSteps,
    createBuySellLimitSteps,
    createBuySellMarketSteps,
    createSellCollectibleSteps,
} from '../../util/steps_modals_generation';
import {
    Collectible,
    Notification,
    NotificationKind,
    OrderFeeData,
    OrderSide,
    Step,
    StepKind,
    StepToggleTokenLock,
    StepWrapEth,
    ThunkCreator,
    Token,
    TokenBalance,
    UserHistory,
    TradeHistory,
    ModalState,
} from '../../util/types';
import * as selectors from '../selectors';

import { LocalStorage } from '../../services/local_storage';
const LocalStorages = new LocalStorage();

export const setHasUnreadNotifications = createAction('ui/UNREAD_NOTIFICATIONS_set', resolve => {
    return (hasUnreadNotifications: boolean) => resolve(hasUnreadNotifications);
});

export const addNotifications = createAction('ui/NOTIFICATIONS_add', resolve => {
    return (newNotifications: Notification[]) => resolve(newNotifications);
});

export const setNotifications = createAction('ui/NOTIFICATIONS_set', resolve => {
    return (notifications: Notification[]) => resolve(notifications);
});

export const setOrderPriceSelected = createAction('ui/ORDER_PRICE_SELECTED_set', resolve => {
    return (orderPriceSelected: BigNumber) => resolve(orderPriceSelected);
});

export const setOrderSelected = createAction('ui/ORDER_SELECTED_set', resolve => {
    return (orderSelected: BigNumber[]) => resolve(orderSelected);
});

export const setStepsModalPendingSteps = createAction('ui/steps_modal/PENDING_STEPS_set', resolve => {
    return (pendingSteps: Step[]) => resolve(pendingSteps);
});

export const setStepsModalDoneSteps = createAction('ui/steps_modal/DONE_STEPS_set', resolve => {
    return (doneSteps: Step[]) => resolve(doneSteps);
});

export const setStepsModalCurrentStep = createAction('ui/steps_modal/CURRENT_STEP_set', resolve => {
    return (currentStep: Step | null) => resolve(currentStep);
});

export const stepsModalAdvanceStep = createAction('ui/steps_modal/advance_step');

export const stepsModalReset = createAction('ui/steps_modal/reset');

export const setModal = createAction('ui/modal/set', resolve => {
    return (modal: ModalState) => resolve(modal);
});
export const closeModal = createAction('ui/modal/close');

export const locationChange = createAction('@@router/LOCATION_CHANGE');

export const startToggleTokenLockSteps: ThunkCreator = (token: Token, isUnlocked: boolean) => {
    return async dispatch => {
        const toggleTokenLockStep = isUnlocked ? getLockTokenStep(token) : getUnlockTokenStep(token);

        dispatch(setStepsModalCurrentStep(toggleTokenLockStep));
        dispatch(setStepsModalPendingSteps([]));
        dispatch(setStepsModalDoneSteps([]));
    };
};

export const startWrapEtherSteps: ThunkCreator = (newWethBalance: BigNumber) => {
    return async (dispatch, getState) => {
        const state = getState();
        const currentWethBalance = selectors.getWethBalance(state);

        const wrapEthStep: StepWrapEth = {
            kind: StepKind.WrapEth,
            currentWethBalance,
            newWethBalance,
            context: 'standalone',
        };

        dispatch(setStepsModalCurrentStep(wrapEthStep));
        dispatch(setStepsModalPendingSteps([]));
        dispatch(setStepsModalDoneSteps([]));
    };
};


export const startSellCollectibleSteps: ThunkCreator = (
    collectible: Collectible,
    startingPrice: BigNumber,
    side: OrderSide,
    expirationDate: BigNumber,
    endingPrice: BigNumber | null,
) => {
    return async (dispatch, getState, { getContractWrappers }) => {
        const state = getState();

        const contractWrappers = await getContractWrappers();
        const ethAccount = selectors.getEthAccount(state);

        const erc721Token = new ERC721TokenContract(COLLECTIBLE_ADDRESS, contractWrappers.getProvider());
        const isUnlocked = await erc721Token
            .isApprovedForAll(ethAccount, contractWrappers.contractAddresses.erc721Proxy)
            .callAsync();
        const sellCollectibleSteps: Step[] = createSellCollectibleSteps(
            collectible,
            startingPrice,
            side,
            isUnlocked,
            expirationDate,
            endingPrice,
        );
        dispatch(setStepsModalCurrentStep(sellCollectibleSteps[0]));
        dispatch(setStepsModalPendingSteps(sellCollectibleSteps.slice(1)));
        dispatch(setStepsModalDoneSteps([]));
    };
};

export const startBuyCollectibleSteps: ThunkCreator = (collectible: Collectible, ethAccount: string) => {
    return async (dispatch, getState, { getContractWrappers, getWeb3Wrapper }) => {
        if (!collectible.order) {
            throw new Error('Collectible is not for sale');
        }

        let buyCollectibleSteps;
        if (isDutchAuction(collectible.order)) {
            throw new Error('DutchAuction currently unsupported');
            // const state = getState();
            // const contractWrappers = await getContractWrappers();

            // const wethTokenBalance = selectors.getWethTokenBalance(state) as TokenBalance;

            // const { currentAmount } = await contractWrappers.dutchAuction.getAuctionDetails.callAsync(
            //     collectible.order,
            // );

            // buyCollectibleSteps = createDutchBuyCollectibleSteps(
            //     collectible.order,
            //     collectible,
            //     wethTokenBalance,
            //     currentAmount,
            // );
        } else {
            buyCollectibleSteps = createBasicBuyCollectibleSteps(collectible.order, collectible);
        }

        dispatch(setStepsModalCurrentStep(buyCollectibleSteps[0]));
        dispatch(setStepsModalPendingSteps(buyCollectibleSteps.slice(1)));
        dispatch(setStepsModalDoneSteps([]));
    };
};

export const startBuySellLimitSteps: ThunkCreator = (
    amount: BigNumber,
    price: BigNumber,
    side: OrderSide,
    orderFeeData: OrderFeeData,
    expire: number,
    takerAddress?: string,
) => {
    return async (dispatch, getState) => {
        const state = getState();
        const baseToken = selectors.getBaseToken(state) as Token;
        const quoteToken = selectors.getQuoteToken(state) as Token;
        const tokenBalances = selectors.getTokenBalances(state) as TokenBalance[];
        const wethTokenBalance = selectors.getWethTokenBalance(state) as TokenBalance;
        const totalEthBalance = selectors.getTotalEthBalance(state);
        const quoteTokenBalance = selectors.getQuoteTokenBalance(state);
        const baseTokenBalance = selectors.getBaseTokenBalance(state);
        const orders = side === OrderSide.Buy ? selectors.getOpenSellOrders(state) : selectors.getOpenBuyOrders(state);
        // sort orders from best to worse
        let sortedOrders = orders.sort((a, b) => {
            if (side === OrderSide.Buy) {
                return a.price.comparedTo(b.price);
            } else {
                return b.price.comparedTo(a.price);
            }
        });
        // Check price valid
        let canBeMarketFilled = false;
        if (side === OrderSide.Buy && sortedOrders.length > 0 && (price.gte(sortedOrders[0].price))) {
            canBeMarketFilled = true;
            sortedOrders = sortedOrders.filter((o) => {
                return o.price.lte(price)
            })
        } else if (side === OrderSide.Sell && sortedOrders.length > 0 && (price.lte(sortedOrders[0].price))) {
            canBeMarketFilled = true;
            sortedOrders = sortedOrders.filter((o) => {
                return o.price.gte(price)
            })
        }
        let amountRemain = new BigNumber(0);
        //canBeMarketFilled = false
        if (canBeMarketFilled) {
            // tslint:disable-next-line:no-unused-variable
            const [ordersToFill, filledAmounts, canBeFilled, amounts1, realBuyAmount] = buildMarketOrders(
                {
                    amount,
                    orders: sortedOrders,
                },
                side,
            );
            let totalFilledAmount = filledAmounts.reduce((total: BigNumber, currentValue: BigNumber) => {
                return total.plus(currentValue);
            }, ZERO);
            if (side === OrderSide.Sell) {
                // When selling, user should have enough BASE Token
                if (baseTokenBalance && baseTokenBalance.balance.isLessThan(totalFilledAmount)) {
                    throw new InsufficientTokenBalanceException(baseToken.symbol);
                }
            } else {
                // if (wethTokenBalance.balance.isLessThan(totalFilledAmount)) {
                //     throw new InsufficientTokenBalanceException(wethTokenBalance.token.symbol);
                // }
                // When buying and
                // if quote token is weth, should have enough ETH + WETH balance, or
                // if quote token is not weth, should have enough quote token balance
                const ifEthAndWethNotEnoughBalance =
                    isWeth(quoteToken.symbol) && totalEthBalance.isLessThan(totalFilledAmount);
                const ifOtherQuoteTokenAndNotEnoughBalance =
                    !isWeth(quoteToken.symbol) &&
                    quoteTokenBalance &&
                    quoteTokenBalance.balance.isLessThan(totalFilledAmount);
                if (ifEthAndWethNotEnoughBalance || ifOtherQuoteTokenAndNotEnoughBalance) {
                    throw new InsufficientTokenBalanceException(quoteToken.symbol);
                }
            }
            if (canBeFilled) {
                dispatch(startBuySellMarketSteps(amount, side, orderFeeData))
            } else {
                totalFilledAmount = side === OrderSide.Sell ? totalFilledAmount : realBuyAmount;
                amountRemain = side === OrderSide.Sell ? amount.minus(totalFilledAmount) : amount.minus(realBuyAmount);
                const buySellLimitFlow: Step[] = createBuySellLimitSteps(
                    baseToken,
                    quoteToken,
                    tokenBalances,
                    wethTokenBalance,
                    amountRemain,
                    price,
                    side,
                    orderFeeData,
                    expire,
                    takerAddress
                );
                dispatch(startBuySellMarketSteps(totalFilledAmount, side, orderFeeData, buySellLimitFlow))
            }
        } else {
            const buySellLimitFlow: Step[] = createBuySellLimitSteps(
                baseToken,
                quoteToken,
                tokenBalances,
                wethTokenBalance,
                amount,
                price,
                side,
                orderFeeData,
                expire,
                takerAddress
            );
            dispatch(setStepsModalCurrentStep(buySellLimitFlow[0]));
            dispatch(setStepsModalPendingSteps(buySellLimitFlow.slice(1)));
            dispatch(setStepsModalDoneSteps([]));
        }
    };
};

export const startBuySellMarketSteps: ThunkCreator = (
    amount: BigNumber,
    side: OrderSide,
    orderFeeData: OrderFeeData,
    buySellLimitFlow?: Step[],
) => {
    return async (dispatch, getState) => {
        const state = getState();
        const baseToken = selectors.getBaseToken(state) as Token;
        const quoteToken = selectors.getQuoteToken(state) as Token;
        const tokenBalances = selectors.getTokenBalances(state) as TokenBalance[];
        const wethTokenBalance = selectors.getWethTokenBalance(state) as TokenBalance;
        const ethBalance = selectors.getEthBalance(state);
        const totalEthBalance = selectors.getTotalEthBalance(state);
        const quoteTokenBalance = selectors.getQuoteTokenBalance(state);
        const baseTokenBalance = selectors.getBaseTokenBalance(state);
        const orders = side === OrderSide.Buy ? selectors.getOpenSellOrders(state) : selectors.getOpenBuyOrders(state);
        // tslint:disable-next-line:no-unused-variable
        const [_ordersToFill, filledAmounts, canBeFilled] = buildMarketOrders(
            {
                amount,
                orders,
            },
            side,
        );
        if (!canBeFilled) {
            throw new InsufficientOrdersAmountException();
        }

        let totalFilledAmount = filledAmounts.reduce((total: BigNumber, currentValue: BigNumber) => {
            return total.plus(currentValue);
        }, ZERO);
        const price = totalFilledAmount.div(amount);
        if (side === OrderSide.Sell) {
            // When selling, user should have enough BASE Token
            if (baseTokenBalance && baseTokenBalance.balance.isLessThan(totalFilledAmount)) {
                throw new InsufficientTokenBalanceException(baseToken.symbol);
            }

        } else {
            // if (wethTokenBalance.balance.isLessThan(totalFilledAmount)) {
            //     throw new InsufficientTokenBalanceException(wethTokenBalance.token.symbol);
            // }
            // When buying and
            // if quote token is weth, should have enough ETH + WETH balance, or
            // if quote token is not weth, should have enough quote token balance
            const ifEthAndWethNotEnoughBalance =
                isWeth(quoteToken.symbol) && totalEthBalance.isLessThan(totalFilledAmount);
            const ifOtherQuoteTokenAndNotEnoughBalance =
                !isWeth(quoteToken.symbol) &&
                quoteTokenBalance &&
                quoteTokenBalance.balance.isLessThan(totalFilledAmount);
            if (ifEthAndWethNotEnoughBalance || ifOtherQuoteTokenAndNotEnoughBalance) {
                throw new InsufficientTokenBalanceException(quoteToken.symbol);
            }
        }

        let buySellMarketFlow: Step[] = createBuySellMarketSteps(
            baseToken,
            quoteToken,
            tokenBalances,
            wethTokenBalance,
            ethBalance,
            amount,
            side,
            price,
            orderFeeData,
        );

        if (buySellLimitFlow) {
            buySellMarketFlow = buySellMarketFlow.concat(buySellLimitFlow);
        }
        dispatch(setStepsModalCurrentStep(buySellMarketFlow[0]));
        dispatch(setStepsModalPendingSteps(buySellMarketFlow.slice(1)));
        dispatch(setStepsModalDoneSteps([]));
    };
};

const getUnlockTokenStep = (token: Token): StepToggleTokenLock => {
    return {
        kind: StepKind.ToggleTokenLock,
        token,
        isUnlocked: false,
        context: 'standalone',
    };
};

const getLockTokenStep = (token: Token): StepToggleTokenLock => {
    return {
        kind: StepKind.ToggleTokenLock,
        token,
        isUnlocked: true,
        context: 'standalone',
    };
};

export const createSignedOrder: ThunkCreator = (amount: BigNumber, price: BigNumber, side: OrderSide, expire: number = 0, takerAddress?: string) => {
    return async (dispatch, getState, { getContractWrappers, getWeb3Wrapper }) => {
        const state = getState();
        const ethAccount = selectors.getEthAccount(state);
        const baseToken = selectors.getBaseToken(state) as Token;
        const quoteToken = selectors.getQuoteToken(state) as Token;
        try {
            const web3Wrapper = await getWeb3Wrapper();
            const contractWrappers = await getContractWrappers();
            const order = await buildLimitOrder(
                {
                    account: ethAccount,
                    amount,
                    price,
                    baseTokenAddress: baseToken.address,
                    quoteTokenAddress: quoteToken.address,
                    exchangeAddress: contractWrappers.exchange.address,
                    expire,
                    takerAddress,
                },
                side,
            );

            const provider = new MetamaskSubprovider(web3Wrapper.getProvider());
            return signatureUtils.ecSignOrderAsync(provider, order, ethAccount);
        } catch (error) {
            throw new SignedOrderException(error.message);
        }
    };
};

export const addMarketBuySellNotification: ThunkCreator = (
    id: string,
    amount: BigNumber,
    token: Token,
    side: OrderSide,
    tx: Promise<any>,
) => {
    return async dispatch => {
        dispatch(
            addNotifications([
                {
                    id,
                    kind: NotificationKind.Market,
                    amount,
                    token,
                    side,
                    tx,
                    timestamp: new Date(),
                },
            ]),
        );
    };
};

export const setThemeUI = createAction('ui/set_theme', resolve => {
    return (theme: string) => resolve(theme);
});

export const setLanguage = createAction('ui/set_language', resolve => {
    return (lang: string) => resolve(lang);
});
export const setThemeUIAction: ThunkCreator = (theme: string) => {
    return async dispatch => {
        LocalStorages.saveTheme(theme);
        dispatch(setThemeUI(theme));
    };
};

export const setLanguageAction: ThunkCreator = (lang: string) => {
    return async dispatch => {
        dispatch(setLanguage(lang));
    };
};


export const getUserHistory = createAction('ui/USER_HISTORY_get', resolve => {
    return (userHistory: UserHistory[]) => resolve(userHistory);
});

export const getUserHistoryFromAPI: ThunkCreator = (
    currentPage: number,
    limit: number,
    baseAssetData?: string,
    quoteAssetData?: string,
) => {
    return async (dispatch, getState) => {
        const state = getState();
        const ethAccount = selectors.getEthAccount(state);
        if (ethAccount && ethAccount.length > 30) {
            try {
                const response = (baseAssetData && quoteAssetData)
                    ? await axios.get(`${USER_TRADE_HISTORY_URL}userAddress=${ethAccount}&page=${currentPage}&perPage=${limit}${baseAssetData && quoteAssetData ? '&baseAssetData=' + baseAssetData + '&quoteAssetData=' + quoteAssetData : ''}`)
                    : await axios.get(`${ALL_USER_TRADE_HISTORY_URL}userAddress=${ethAccount}&page=${currentPage}&perPage=${limit}`);
                dispatch(
                    getUserHistory(response.data),
                );
            } catch (error) {
                // console.error(error);
            }
        }
    };
};

export const getTradeHistory = createAction('ui/TRADE_HISTORY_get', resolve => {
    return (tradeHistory: TradeHistory[]) => resolve(tradeHistory);
});

export const addTradeHistory = createAction('ui/TRADE_HISTORY_add', resolve => {
    return (tradeHistory: TradeHistory[]) => resolve(tradeHistory);
});

export const getTradeHistoryFromAPI: ThunkCreator = (
    baseAssetData: string,
    quoteAssetData: string
) => {
    return async (dispatch) => {
        try {
            const response = await axios.get(`${TRADE_HISTORY_URL}baseAssetData=${baseAssetData}&quoteAssetData=${quoteAssetData}&page=${1}&perPage=${100}`);
            if (response && response.data && response.data.records) {
                dispatch(
                    getTradeHistory(response.data.records),
                );
            }
        } catch (error) {
            // console.error(error);
        }
    };
};
